/**
 * Excel (.xlsx) export για τον Πίνακα Αναλυτικών.
 *
 * Ίδια λογική με το PDF (exportCustomReportPDF): ο χρήστης διαλέγει ενότητες
 * (Λογιστικά, Product Mix, Πελάτες, Κανάλια) και τα δεδομένα έρχονται πλήρη από
 * το /report-data. Κάθε ενότητα γίνεται ξεχωριστό φύλλο, styled, με πραγματικούς
 * αριθμούς (ώστε ο χρήστης να μπορεί να τους αθροίσει/φιλτράρει) και «ωραία
 * γραφήματα».
 *
 * Σημείωση για τα γραφήματα: καμία browser βιβλιοθήκη (ExcelJS/SheetJS) δεν
 * υποστηρίζει native Excel charts. Τα σχεδιάζουμε σε <canvas> (donut / μπάρες)
 * και τα ενσωματώνουμε ως εικόνες — έτσι έχουν ακριβώς την εμφάνιση που θέλουμε
 * και δουλεύουν παντού (Excel, LibreOffice, Google Sheets).
 */
import type ExcelJS from 'exceljs'; // τύποι μόνο — το runtime φορτώνεται lazy (βλ. import() πιο κάτω)
import i18next from 'i18next';
import { ReportOptions } from './exportAnalytics';

// ── Χρώματα brand (ARGB) ──
const C = {
  navy: 'FF1E3A8A', blue: 'FF2563EB', green: 'FF16A34A', purple: 'FF7C3AED',
  cyan: 'FF0891B2', amber: 'FFD97706', slate: 'FF475569', headText: 'FFFFFFFF',
  subHead: 'FFF1F5F9', border: 'FFE2E8F0', zebra: 'FFFAFBFC', total: 'FFEEF2FF',
  ink: 'FF0F172A',
};
// Ίδια χρώματα ως CSS (για τα canvas γραφήματα)
const CSS = {
  blue: '#2563eb', green: '#16a34a', purple: '#7c3aed', cyan: '#0891b2',
  amber: '#d97706', slate: '#64748b', track: '#e2e8f0', ink: '#0f172a', muted: '#94a3b8',
};
const PIE = ['#2563eb', '#16a34a', '#d97706', '#7c3aed', '#0891b2', '#db2777', '#ca8a04', '#0d9488', '#dc2626', '#4f46e5'];

const EUR_FMT = '#,##0.00" €"';
const INT_FMT = '#,##0';
const PCT_FMT = '0.0%';

const CHANNEL_LABELS: Record<string, string> = { takeaway: 'Take away', delivery: 'Delivery', dine_in: 'Dine-in' };
const PLATFORM_LABELS: Record<string, string> = { efood: 'efood', wolt: 'Wolt', box: 'Box', own: 'Κατάστημα' };
const DOC_TYPE_LABELS: Record<string, string> = {
  '11.1': 'Απόδειξη', '11.2': 'Απόδ. Υπηρεσίας', '1.1': 'Τιμολόγιο',
  '2.1': 'Τιμ. Υπηρεσίας', '8.6': 'Δελτίο Παραγγελίας', '9.3': 'Δελτίο Αποστολής', '11.4': 'Ακυρωτικό', '5.1': 'Πιστωτικό',
};

const lang = () => i18next.language || 'el';
const fmtDate = (d: string) => (d ? new Date(d).toLocaleDateString(lang() === 'el' ? 'el-GR' : 'en-GB') : '');
const num = (n: any) => Number(n) || 0;
const eurTxt = (n: any) => `${num(n).toFixed(2)} €`;

// ─────────────────────────────────────────────────────────────────────────────
// Canvas γραφήματα → dataURL (PNG). Σχεδιάζουμε σε 2x για ευκρίνεια.
// ─────────────────────────────────────────────────────────────────────────────
interface Slice { label: string; value: number; color?: string; }

function newCanvas(w: number, h: number) {
  const S = 2;
  const c = document.createElement('canvas');
  c.width = w * S; c.height = h * S;
  const x = c.getContext('2d')!;
  x.scale(S, S);
  x.fillStyle = '#ffffff'; x.fillRect(0, 0, w, h);
  x.textBaseline = 'middle';
  return { c, x, w, h };
}
const F = (px: number, weight = '400') => `${weight} ${px}px -apple-system, "Segoe UI", Arial, sans-serif`;

function donutChart(title: string, items: Slice[], valueFmt: (v: number) => string): string {
  const data = items.filter(i => num(i.value) > 0);
  const W = 380, H = 230;
  const { c, x } = newCanvas(W, H);
  x.fillStyle = CSS.ink; x.font = F(14, '700'); x.fillText(title, 16, 20);
  const total = data.reduce((s, i) => s + num(i.value), 0) || 1;
  const cx = 100, cy = 135, r = 70, ir = 42;
  let a = -Math.PI / 2;
  data.forEach((it, i) => {
    const frac = num(it.value) / total;
    const a2 = a + frac * 2 * Math.PI;
    x.beginPath(); x.moveTo(cx, cy); x.arc(cx, cy, r, a, a2); x.closePath();
    x.fillStyle = it.color || PIE[i % PIE.length]; x.fill();
    a = a2;
  });
  x.beginPath(); x.arc(cx, cy, ir, 0, 2 * Math.PI); x.fillStyle = '#fff'; x.fill();
  x.fillStyle = CSS.ink; x.font = F(15, '800'); x.textAlign = 'center';
  x.fillText(valueFmt(total), cx, cy - 4);
  x.fillStyle = CSS.muted; x.font = F(9, '600'); x.fillText('ΣΥΝΟΛΟ', cx, cy + 12);
  x.textAlign = 'left';
  // legend
  let ly = 54;
  data.slice(0, 6).forEach((it, i) => {
    x.fillStyle = it.color || PIE[i % PIE.length];
    x.beginPath(); (x as any).roundRect ? (x as any).roundRect(200, ly - 6, 12, 12, 3) : x.rect(200, ly - 6, 12, 12); x.fill();
    x.fillStyle = CSS.ink; x.font = F(10.5, '600');
    const pct = ((num(it.value) / total) * 100).toFixed(1);
    let label = it.label.length > 20 ? it.label.slice(0, 19) + '…' : it.label;
    x.fillText(`${label}`, 218, ly);
    x.fillStyle = CSS.muted; x.font = F(10, '600');
    x.fillText(`${valueFmt(num(it.value))} · ${pct}%`, 218, ly + 13);
    ly += 30;
  });
  return c.toDataURL('image/png');
}

function barsH(title: string, items: Slice[], valueFmt: (v: number) => string): string {
  const data = items.filter(i => num(i.value) !== 0).slice(0, 8);
  const rowH = 26, W = 380, H = 44 + data.length * rowH;
  const { c, x } = newCanvas(W, H);
  x.fillStyle = CSS.ink; x.font = F(14, '700'); x.fillText(title, 16, 20);
  const max = Math.max(...data.map(i => num(i.value)), 1);
  const x0 = 16, barX = 130, barW = W - barX - 70;
  data.forEach((it, i) => {
    const y = 44 + i * rowH;
    x.fillStyle = CSS.ink; x.font = F(10.5, '600');
    let label = it.label.length > 18 ? it.label.slice(0, 17) + '…' : it.label;
    x.fillText(label, x0, y + 8);
    x.fillStyle = CSS.track;
    const bg: any = x; bg.beginPath();
    (x as any).roundRect ? (x as any).roundRect(barX, y, barW, 14, 7) : x.rect(barX, y, barW, 14); x.fill();
    const w = Math.max(3, (num(it.value) / max) * barW);
    x.fillStyle = it.color || PIE[i % PIE.length];
    x.beginPath();
    (x as any).roundRect ? (x as any).roundRect(barX, y, w, 14, 7) : x.rect(barX, y, w, 14); x.fill();
    x.fillStyle = CSS.ink; x.font = F(10, '700'); x.textAlign = 'right';
    x.fillText(valueFmt(num(it.value)), W - 8, y + 8);
    x.textAlign = 'left';
  });
  return c.toDataURL('image/png');
}

function barsV(title: string, items: Slice[], valueFmt: (v: number) => string): string {
  const data = items.slice(-14); // τελευταίες ~14 μέρες
  const W = Math.max(380, 40 + data.length * 26), H = 210;
  const { c, x } = newCanvas(W, H);
  x.fillStyle = CSS.ink; x.font = F(14, '700'); x.fillText(title, 16, 20);
  const max = Math.max(...data.map(i => num(i.value)), 1);
  const baseY = 165, top = 44, x0 = 30;
  const slot = (W - x0 - 12) / data.length;
  const bw = Math.min(20, slot * 0.6);
  data.forEach((it, i) => {
    const h = Math.max(2, (num(it.value) / max) * (baseY - top));
    const bx = x0 + i * slot + (slot - bw) / 2;
    const grad = x.createLinearGradient(0, baseY - h, 0, baseY);
    grad.addColorStop(0, CSS.blue); grad.addColorStop(1, '#93c5fd');
    x.fillStyle = grad;
    x.beginPath();
    (x as any).roundRect ? (x as any).roundRect(bx, baseY - h, bw, h, 3) : x.rect(bx, baseY - h, bw, h); x.fill();
    x.fillStyle = CSS.muted; x.font = F(8, '600'); x.textAlign = 'center';
    x.fillText(it.label, bx + bw / 2, baseY + 12);
    x.textAlign = 'left';
  });
  x.strokeStyle = CSS.track; x.beginPath(); x.moveTo(x0 - 6, baseY); x.lineTo(W - 6, baseY); x.stroke();
  x.fillStyle = CSS.muted; x.font = F(9, '600'); x.fillText(valueFmt(max), 2, top);
  return c.toDataURL('image/png');
}

// ─────────────────────────────────────────────────────────────────────────────
// Βοηθητικά ExcelJS
// ─────────────────────────────────────────────────────────────────────────────
type Align = 'left' | 'right' | 'center';
interface Col { header: string; key: string; width: number; align?: Align; fmt?: string; }

function makeSheet(wb: ExcelJS.Workbook, name: string, accent: string) {
  const ws = wb.addWorksheet(name, {
    views: [{ showGridLines: false }],
    properties: { defaultRowHeight: 18 },
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0, margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 } },
  });
  ws.properties.tabColor = { argb: accent };
  return ws;
}

function titleBlock(ws: ExcelJS.Worksheet, r: number, span: number, title: string, subtitle: string, accent: string) {
  ws.mergeCells(r, 1, r, span);
  const cell = ws.getCell(r, 1);
  cell.value = title;
  cell.font = { bold: true, size: 16, color: { argb: C.headText } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accent } };
  cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(r).height = 30;
  ws.mergeCells(r + 1, 1, r + 1, span);
  const sub = ws.getCell(r + 1, 1);
  sub.value = subtitle;
  sub.font = { size: 10, color: { argb: C.slate } };
  sub.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  ws.getRow(r + 1).height = 18;
  return r + 3;
}

function sectionLabel(ws: ExcelJS.Worksheet, r: number, span: number, text: string, accent: string) {
  ws.mergeCells(r, 1, r, span);
  const cell = ws.getCell(r, 1);
  cell.value = text.toUpperCase();
  cell.font = { bold: true, size: 10.5, color: { argb: accent } };
  cell.alignment = { vertical: 'middle', horizontal: 'left' };
  ws.getRow(r).height = 22;
  return r + 1;
}

/**
 * Γράφει έναν πίνακα με header + γραμμές. Επιστρέφει την επόμενη ελεύθερη γραμμή.
 * rows: πίνακας από αντικείμενα με κλειδιά == cols[].key.
 */
function writeTable(ws: ExcelJS.Worksheet, startRow: number, startCol: number, cols: Col[], rows: any[], accent: string, opts?: { totalRow?: any }): number {
  let r = startRow;
  // header
  const headRow = ws.getRow(r);
  cols.forEach((col, i) => {
    const cell = headRow.getCell(startCol + i);
    cell.value = col.header;
    cell.font = { bold: true, size: 9.5, color: { argb: C.headText } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accent } };
    cell.alignment = { vertical: 'middle', horizontal: col.align || 'left' };
    cell.border = { bottom: { style: 'thin', color: { argb: accent } } };
  });
  headRow.height = 20;
  r++;
  // body
  rows.forEach((row, ri) => {
    const dataRow = ws.getRow(r);
    cols.forEach((col, i) => {
      const cell = dataRow.getCell(startCol + i);
      cell.value = row[col.key];
      cell.alignment = { vertical: 'middle', horizontal: col.align || 'left' };
      if (col.fmt) cell.numFmt = col.fmt;
      cell.font = { size: 10, color: { argb: C.ink } };
      if (ri % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.zebra } };
      cell.border = { bottom: { style: 'hair', color: { argb: C.border } } };
    });
    r++;
  });
  // total row
  if (opts?.totalRow) {
    const totRow = ws.getRow(r);
    cols.forEach((col, i) => {
      const cell = totRow.getCell(startCol + i);
      cell.value = opts.totalRow[col.key];
      cell.alignment = { vertical: 'middle', horizontal: col.align || 'left' };
      if (col.fmt && opts.totalRow[col.key] != null) cell.numFmt = col.fmt;
      cell.font = { bold: true, size: 10, color: { argb: C.navy } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.total } };
      cell.border = { top: { style: 'thin', color: { argb: C.blue } } };
    });
    r++;
  }
  return r + 1;
}

function setWidths(ws: ExcelJS.Worksheet, widths: number[]) {
  widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
}

// KPI κάρτες σε σειρά (merged cells με χρωματιστό border-top look)
function kpiRow(ws: ExcelJS.Worksheet, r: number, startCol: number, kpis: { label: string; value: string; accent: string }[]): number {
  const perKpi = 3;
  kpis.forEach((k, i) => {
    const c0 = startCol + i * perKpi;
    // label
    ws.mergeCells(r, c0, r, c0 + perKpi - 1);
    const lc = ws.getCell(r, c0);
    lc.value = k.label.toUpperCase();
    lc.font = { bold: true, size: 8.5, color: { argb: C.slate } };
    lc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    lc.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    lc.border = { top: { style: 'medium', color: { argb: k.accent } }, left: { style: 'thin', color: { argb: C.border } }, right: { style: 'thin', color: { argb: C.border } } };
    // value
    ws.mergeCells(r + 1, c0, r + 1, c0 + perKpi - 1);
    const vc = ws.getCell(r + 1, c0);
    vc.value = k.value;
    vc.font = { bold: true, size: 15, color: { argb: C.ink } };
    vc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
    vc.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    vc.border = { bottom: { style: 'thin', color: { argb: C.border } }, left: { style: 'thin', color: { argb: C.border } }, right: { style: 'thin', color: { argb: C.border } } };
    ws.getRow(r).height = 16;
    ws.getRow(r + 1).height = 26;
  });
  return r + 3;
}

function addChart(wb: ExcelJS.Workbook, ws: ExcelJS.Worksheet, dataUrl: string, col: number, row: number, width: number, height: number) {
  const id = wb.addImage({ base64: dataUrl, extension: 'png' });
  ws.addImage(id, { tl: { col, row } as any, ext: { width, height } });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────
interface Params {
  analytics: any;
  report: any;
  options: ReportOptions;
  storeName?: string;
  dateFrom: string;
  dateTo: string;
}

export async function exportCustomReportExcel({ analytics, report, options, storeName, dateFrom, dateTo }: Params) {
  const ExcelJSRuntime = (await import('exceljs')).default;
  const wb = new ExcelJSRuntime.Workbook();
  wb.creator = 'Xentrix Flow';
  wb.created = new Date();
  const period = `${fmtDate(dateFrom)} – ${fmtDate(dateTo)}`;
  const o = analytics?.orders || {};

  // ── 1. ΣΥΝΟΨΗ ──────────────────────────────────────────────────────────────
  {
    const ws = makeSheet(wb, 'Σύνοψη', C.blue);
    setWidths(ws, [26, 14, 14, 14, 6, 3, 3, 3, 3, 3]);
    let r = titleBlock(ws, 1, 4, storeName || 'Κατάστημα', `Αναφορά Αναλυτικών · ${period}`, C.navy);
    const cmp = o.comparison || {};
    const dch = (v: number) => (v == null || isNaN(v) || v === 0 ? '' : `${v > 0 ? '▲' : '▼'} ${Math.abs(v).toFixed(1)}%`);
    r = kpiRow(ws, r, 1, [
      { label: 'Παραγγελίες', value: `${o.total_orders ?? 0}  ${dch(cmp.ordersChange)}`.trim(), accent: C.blue },
      { label: 'Τζίρος', value: `${eurTxt(o.total_revenue)}  ${dch(cmp.revenueChange)}`.trim(), accent: C.green },
      { label: 'Μέση παραγγελία', value: eurTxt(o.average_order_value), accent: C.purple },
    ]);

    const daily = (o.daily_trend || []) as any[];
    const chartRow = r;
    if (daily.length) {
      addChart(wb, ws, barsV('Ημερήσιος τζίρος', daily.map(d => ({ label: fmtDate(d.date).slice(0, 5), value: num(d.revenue) })), eurTxt), 0, chartRow - 1, 460, 210);
    }
    const pm = (o.payment_methods || []) as any[];
    if (pm.length) {
      addChart(wb, ws, donutChart('Τρόποι πληρωμής', pm.map(p => ({ label: p.method, value: num(p.amount) })), eurTxt), 5, chartRow - 1, 380, 230);
    }
    r = chartRow + 12;

    if (daily.length) {
      r = sectionLabel(ws, r, 4, 'Ημερήσια εξέλιξη', C.blue);
      r = writeTable(ws, r, 1, [
        { header: 'Ημερομηνία', key: 'date', width: 26 },
        { header: 'Παραγγελίες', key: 'orders', width: 14, align: 'right', fmt: INT_FMT },
        { header: 'Τζίρος', key: 'revenue', width: 14, align: 'right', fmt: EUR_FMT },
      ], daily.map(d => ({ date: fmtDate(d.date), orders: num(d.orders), revenue: num(d.revenue) })), C.blue, {
        totalRow: { date: 'Σύνολο', orders: daily.reduce((s, d) => s + num(d.orders), 0), revenue: daily.reduce((s, d) => s + num(d.revenue), 0) },
      });
    }
  }

  // ── 2. ΛΟΓΙΣΤΙΚΑ ────────────────────────────────────────────────────────────
  const acc = report?.accounting;
  if (options.accounting && acc) {
    const ws = makeSheet(wb, 'Λογιστικά', C.green);
    setWidths(ws, [24, 12, 12, 12, 12, 14, 4, 3, 3, 3]);
    let r = titleBlock(ws, 1, 6, 'Λογιστικά', `Εισπράξεις & παραστατικά · ${period}`, C.green);

    const payItems = [
      { label: 'Μετρητά', key: 'cash', color: '#16a34a' },
      { label: 'Κάρτα', key: 'card', color: '#ca8a04' },
      { label: 'IRIS', key: 'iris', color: '#0891b2' },
      { label: 'Προπληρωμένες / Πλατφόρμες', key: 'prepaid', color: '#7c3aed' },
    ].map(p => ({ label: p.label, value: num(acc[p.key]), color: p.color })).filter(p => Math.abs(p.value) > 0.001);

    const chartRow = r;
    addChart(wb, ws, donutChart('Εισπράξεις ανά τρόπο', payItems, eurTxt), 0, chartRow - 1, 380, 230);
    if ((acc.byPlatform || []).length) {
      addChart(wb, ws, barsH('Τζίρος ανά πλατφόρμα', (acc.byPlatform).map((p: any) => ({ label: PLATFORM_LABELS[p.platform] || p.platform, value: num(p.gross) })), eurTxt), 5, chartRow - 1, 380, 230);
    }
    r = chartRow + 12;

    r = sectionLabel(ws, r, 3, 'Εισπράξεις ανά τρόπο πληρωμής', C.green);
    r = writeTable(ws, r, 1, [
      { header: 'Τρόπος', key: 'l', width: 24 },
      { header: 'Ποσό', key: 'v', width: 14, align: 'right', fmt: EUR_FMT },
      { header: 'Μερίδιο', key: 'p', width: 12, align: 'right', fmt: PCT_FMT },
    ], payItems.map(p => ({ l: p.label, v: p.value, p: num(acc.gross) > 0 ? p.value / num(acc.gross) : 0 })), C.green, {
      totalRow: { l: 'Σύνολο εισπράξεων', v: num(acc.gross), p: null },
    });

    if ((acc.byPlatform || []).length) {
      r = sectionLabel(ws, r, 7, 'Ανά πλατφόρμα', C.green);
      r = writeTable(ws, r, 1, [
        { header: 'Πηγή', key: 'src', width: 20 },
        { header: 'Παραγγ.', key: 'count', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Μετρητά', key: 'cash', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Κάρτα', key: 'card', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'IRIS', key: 'iris', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Προπληρ.', key: 'prepaid', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Σύνολο', key: 'gross', width: 14, align: 'right', fmt: EUR_FMT },
      ], (acc.byPlatform).map((p: any) => ({ src: PLATFORM_LABELS[p.platform] || p.platform, count: num(p.count), cash: num(p.cash), card: num(p.card), iris: num(p.iris), prepaid: num(p.prepaid), gross: num(p.gross) })), C.green, {
        totalRow: { src: 'Σύνολο', count: num(acc.orders), cash: num(acc.cash), card: num(acc.card), iris: num(acc.iris), prepaid: num(acc.prepaid), gross: num(acc.gross) },
      });
    }

    if ((acc.documentTypes || []).length) {
      r = sectionLabel(ws, r, 5, 'Ανά τύπο παραστατικού', C.green);
      r = writeTable(ws, r, 1, [
        { header: 'Παραστατικό', key: 'doc', width: 24 },
        { header: 'Πλήθος', key: 'count', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Καθαρό', key: 'net', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'ΦΠΑ', key: 'vat', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Σύνολο', key: 'gross', width: 14, align: 'right', fmt: EUR_FMT },
      ], (acc.documentTypes).map((d: any) => ({ doc: DOC_TYPE_LABELS[d.code] || d.code, count: num(d.count), net: num(d.net), vat: num(d.vat), gross: num(d.gross) })), C.green);
    }

    if ((acc.byVat || []).length) {
      r = sectionLabel(ws, r, 4, 'Ανάλυση ΦΠΑ', C.green);
      r = writeTable(ws, r, 1, [
        { header: 'Συντελεστής', key: 'rate', width: 16 },
        { header: 'Καθαρό', key: 'net', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'ΦΠΑ', key: 'vat', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Σύνολο', key: 'gross', width: 14, align: 'right', fmt: EUR_FMT },
      ], (acc.byVat).map((v: any) => ({ rate: v.rate != null ? `ΦΠΑ ${v.rate}%` : '—', net: num(v.net), vat: num(v.vat), gross: num(v.gross) })), C.green);
    }

    if ((acc.fees || []).length) {
      r = sectionLabel(ws, r, 3, 'Τέλη & Εισφορές', C.green);
      r = writeTable(ws, r, 1, [
        { header: 'Τέλος', key: 'label', width: 30 },
        { header: 'Τεμ.', key: 'units', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Ποσό', key: 'amount', width: 12, align: 'right', fmt: EUR_FMT },
      ], (acc.fees).map((f: any) => ({ label: f.label, units: num(f.units), amount: num(f.amount) })), C.green,
        (acc.fees).length > 1 ? { totalRow: { label: 'Σύνολο τελών', units: null, amount: num(acc.plasticFees) } } : undefined);
    }
  }

  // ── 3. PRODUCT MIX ─────────────────────────────────────────────────────────
  const prod = report?.products;
  if (options.products && prod) {
    const ws = makeSheet(wb, 'Product Mix', C.purple);
    setWidths(ws, [30, 18, 10, 14, 12, 3, 3, 3, 3, 3]);
    let r = titleBlock(ws, 1, 5, 'Product Mix', `Κατηγορίες & προϊόντα · ${period}`, C.purple);

    const cats = (prod.categories || []) as any[];
    if (cats.length) {
      addChart(wb, ws, barsH('Top κατηγορίες (τζίρος)', cats.map(c => ({ label: c.category, value: num(c.revenue), color: '#7c3aed' })), eurTxt), 0, r - 1, 420, 44 + Math.min(8, cats.length) * 26);
      r += Math.max(11, Math.min(8, cats.length) + 3);
      const catTot = cats.reduce((s, c) => s + num(c.revenue), 0);
      r = sectionLabel(ws, r, 4, 'Κατηγορίες', C.purple);
      r = writeTable(ws, r, 1, [
        { header: 'Κατηγορία', key: 'category', width: 30 },
        { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Τζίρος', key: 'revenue', width: 14, align: 'right', fmt: EUR_FMT },
        { header: 'Μερίδιο', key: 'share', width: 12, align: 'right', fmt: PCT_FMT },
      ], cats.map(c => ({ category: c.category, qty: num(c.qty), revenue: num(c.revenue), share: catTot > 0 ? num(c.revenue) / catTot : 0 })), C.purple, {
        totalRow: { category: 'Σύνολο', qty: cats.reduce((s, c) => s + num(c.qty), 0), revenue: catTot, share: null },
      });
    }

    // Προσφορές ανά deal × πλατφόρμα — «Φορές» = πόσες φορές δόθηκε το πακέτο
    // (στα efood/box deals μετριέται ανά παραγγελία, στη Wolt ανά γραμμή deal).
    const offers = (prod.offers || []) as any[];
    if (offers.length) {
      r = sectionLabel(ws, r, 4, 'Προσφορές (πακέτα) ανά πλατφόρμα', C.purple);
      r = writeTable(ws, r, 1, [
        { header: 'Προσφορά', key: 'offer', width: 44 },
        { header: 'Πλατφόρμα', key: 'platform', width: 12 },
        { header: 'Φορές', key: 'times', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Τζίρος', key: 'revenue', width: 14, align: 'right', fmt: EUR_FMT },
      ], offers.map(o => ({
        offer: o.offer,
        platform: PLATFORM_LABELS[o.platform] || o.platform,
        times: num(o.times),
        revenue: num(o.revenue),
      })), C.purple, offers.length > 1 ? {
        totalRow: {
          offer: 'Σύνολο', platform: null,
          times: offers.reduce((s, o) => s + num(o.times), 0),
          revenue: offers.reduce((s, o) => s + num(o.revenue), 0),
        },
      } : undefined);
    }

    const all = (prod.all || []) as any[];
    const prodTot = all.reduce((s, p) => s + num(p.revenue), 0);
    r = sectionLabel(ws, r, 5, `Όλα τα προϊόντα (${all.length})`, C.purple);
    r = writeTable(ws, r, 1, [
      { header: '#', key: 'rank', width: 5, align: 'right' },
      { header: 'Προϊόν', key: 'name', width: 30 },
      { header: 'Κατηγορία', key: 'category', width: 18 },
      { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
      { header: 'Τζίρος', key: 'revenue', width: 14, align: 'right', fmt: EUR_FMT },
      { header: 'Μερίδιο', key: 'share', width: 12, align: 'right', fmt: PCT_FMT },
    ], all.map((p, i) => ({ rank: i + 1, name: p.name, category: p.category, qty: num(p.qty), revenue: num(p.revenue), share: prodTot > 0 ? num(p.revenue) / prodTot : 0 })), C.purple);

    if (options.detail === 'full') {
      const sizes = (prod.sizes || []) as any[];
      if (sizes.length) {
        r = sectionLabel(ws, r, 3, 'Μεγέθη', C.purple);
        r = writeTable(ws, r, 1, [
          { header: 'Προϊόν', key: 'product', width: 30 },
          { header: 'Μέγεθος', key: 'size', width: 18 },
          { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        ], sizes.map(s => ({ product: s.product, size: s.size, qty: num(s.qty) })), C.purple);
      }
      const opts2 = (prod.options || []) as any[];
      if (opts2.length) {
        r = sectionLabel(ws, r, 3, 'Επιλογές (options)', C.purple);
        r = writeTable(ws, r, 1, [
          { header: 'Ομάδα', key: 'group_name', width: 30 },
          { header: 'Επιλογή', key: 'choice', width: 18 },
          { header: 'Φορές', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        ], opts2.map(op => ({ group_name: op.group_name, choice: op.choice, qty: num(op.qty) })), C.purple);
      }
      const extras = (prod.extras || []) as any[];
      if (extras.length) {
        r = sectionLabel(ws, r, 3, 'Extras / Υλικά', C.purple);
        r = writeTable(ws, r, 1, [
          { header: 'Extra', key: 'name', width: 30 },
          { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
          { header: 'Τζίρος', key: 'revenue', width: 14, align: 'right', fmt: EUR_FMT },
        ], extras.map(e => ({ name: e.name, qty: num(e.qty), revenue: num(e.revenue) })), C.purple);
      }
    }
  }

  // ── 4. ΠΕΛΑΤΕΣ ──────────────────────────────────────────────────────────────
  const cu = report?.customers;
  if (options.customers && cu) {
    const ws = makeSheet(wb, 'Πελάτες', C.cyan);
    setWidths(ws, [28, 16, 10, 14, 12, 3, 3, 3, 3, 3]);
    const s = cu.summary || {}; const ret = cu.retention || {};
    let r = titleBlock(ws, 1, 5, 'Ανάλυση Πελατών', `Πελατολόγιο & συχνότητα · ${period}`, C.cyan);
    r = kpiRow(ws, r, 1, [
      { label: 'Σύνολο πελατών', value: `${s.total_customers ?? 0}`, accent: C.blue },
      { label: 'Νέοι στην περίοδο', value: `${s.new_in_period ?? 0}`, accent: C.green },
      { label: 'Επαναλαμβανόμενοι', value: `${ret.returning ?? 0}`, accent: C.purple },
    ]);

    const freq = (cu.order_frequency || []) as any[];
    if (freq.length) {
      addChart(wb, ws, donutChart('Συχνότητα παραγγελιών', freq.map(f => ({ label: f.bucket, value: num(f.count) })), (v) => `${v}`), 0, r - 1, 380, 230);
      r += 12;
    }

    const top = (cu.top_by_revenue || []) as any[];
    if (top.length) {
      r = sectionLabel(ws, r, 5, 'Κορυφαίοι πελάτες (τζίρος)', C.cyan);
      r = writeTable(ws, r, 1, [
        { header: 'Πελάτης', key: 'name', width: 28 },
        { header: 'Τηλέφωνο', key: 'phone', width: 16 },
        { header: 'Παραγγ.', key: 'orders', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Σύνολο', key: 'spent', width: 14, align: 'right', fmt: EUR_FMT },
        { header: 'Μ.Ο.', key: 'aov', width: 12, align: 'right', fmt: EUR_FMT },
      ], top.map(c => ({ name: c.name || '—', phone: c.phone || '', orders: num(c.total_orders), spent: num(c.total_spent), aov: num(c.average_order_value) })), C.cyan);
    }
  }

  // ── 5. ΚΑΝΑΛΙΑ ──────────────────────────────────────────────────────────────
  const ch = report?.channels;
  if (options.channels && ch) {
    const ws = makeSheet(wb, 'Κανάλια', C.amber);
    setWidths(ws, [22, 12, 12, 14, 12, 3, 3, 3, 3, 3]);
    let r = titleBlock(ws, 1, 5, 'Κανάλια Εξυπηρέτησης', `Take away / Delivery / πλατφόρμες · ${period}`, C.amber);

    const byCh = (ch.byChannel || []) as any[];
    const byPl = (ch.byPlatform || []) as any[];
    const chartRow = r;
    if (byCh.length) addChart(wb, ws, donutChart('Τζίρος ανά κανάλι', byCh.map(c => ({ label: CHANNEL_LABELS[c.channel] || c.channel, value: num(c.gross) })), eurTxt), 0, chartRow - 1, 380, 230);
    if (byPl.length) addChart(wb, ws, barsH('Τζίρος ανά πηγή', byPl.map(p => ({ label: PLATFORM_LABELS[p.platform] || p.platform, value: num(p.gross), color: '#0891b2' })), eurTxt), 5, chartRow - 1, 380, 230);
    r = chartRow + 12;

    if (byCh.length) {
      const tot = byCh.reduce((sm, c) => sm + num(c.gross), 0);
      r = sectionLabel(ws, r, 5, 'Κανάλια εξυπηρέτησης', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Κανάλι', key: 'channel', width: 22 },
        { header: 'Παραγγ.', key: 'count', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Μ.Ο.', key: 'aov', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Τζίρος', key: 'gross', width: 14, align: 'right', fmt: EUR_FMT },
        { header: 'Μερίδιο', key: 'share', width: 12, align: 'right', fmt: PCT_FMT },
      ], byCh.map(c => ({ channel: CHANNEL_LABELS[c.channel] || c.channel, count: num(c.count), aov: num(c.aov), gross: num(c.gross), share: tot > 0 ? num(c.gross) / tot : 0 })), C.amber, {
        totalRow: { channel: 'Σύνολο', count: byCh.reduce((sm, c) => sm + num(c.count), 0), aov: null, gross: tot, share: null },
      });
    }
    if (byPl.length) {
      r = sectionLabel(ws, r, 5, 'Πηγές παραγγελιών', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Πηγή', key: 'platform', width: 22 },
        { header: 'Παραγγ.', key: 'count', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Μ.Ο.', key: 'aov', width: 12, align: 'right', fmt: EUR_FMT },
        { header: 'Τζίρος', key: 'gross', width: 14, align: 'right', fmt: EUR_FMT },
      ], byPl.map(p => ({ platform: PLATFORM_LABELS[p.platform] || p.platform, count: num(p.count), aov: num(p.aov), gross: num(p.gross) })), C.amber);
    }
  }

  // ── 6. ΦΥΡΑ ─────────────────────────────────────────────────────────────────
  // Αξία σε τιμές πώλησης — ξεχωριστό φύλλο, δεν συμμετέχει στα χρηματικά σύνολα.
  const wa = report?.waste;
  if ((options as any).waste && wa && wa.summary) {
    const W_REASONS: Record<string, string> = { expired: 'Ληγμένο', damaged: 'Κατεστραμμένο', misprep: 'Λάθος παρασκευή', other: 'Άλλο' };
    const ws = makeSheet(wb, 'Φύρα', C.amber);
    setWidths(ws, [30, 12, 14, 12, 3, 3, 3, 3, 3, 3]);
    let r = titleBlock(ws, 1, 4, 'Φύρα', `Αξία σε τιμές πώλησης · ${period}`, C.amber);
    r = kpiRow(ws, r, 1, [
      { label: 'Καταχωρήσεις', value: `${wa.summary.entries}`, accent: C.amber },
      { label: 'Τεμάχια', value: `${wa.summary.qty}`, accent: C.amber },
      { label: 'Αξία', value: `€${num(wa.summary.value).toFixed(2)}`, accent: C.amber },
    ]);

    const wProd = (wa.byProduct || []) as any[];
    if (wProd.length) {
      addChart(wb, ws, barsH('Αξία φύρας ανά προϊόν', wProd.slice(0, 8).map((p: any) => ({ label: p.product_name, value: num(p.value), color: '#ea580c' })), eurTxt), 0, r - 1, 420, 44 + Math.min(8, wProd.length) * 26);
      r += Math.max(11, Math.min(8, wProd.length) + 3);
      r = sectionLabel(ws, r, 4, 'Ανά προϊόν', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Προϊόν', key: 'name', width: 30 },
        { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Αξία', key: 'value', width: 14, align: 'right', fmt: EUR_FMT },
        { header: 'Μερίδιο', key: 'share', width: 12, align: 'right', fmt: PCT_FMT },
      ], wProd.map((p: any) => ({ name: p.product_name, qty: num(p.qty), value: num(p.value), share: wa.summary.value > 0 ? num(p.value) / wa.summary.value : 0 })), C.amber, {
        totalRow: { name: 'Σύνολο', qty: num(wa.summary.qty), value: num(wa.summary.value), share: null },
      });
    }

    const wStore = (wa.byStore || []) as any[];
    if (wStore.length) {
      r = sectionLabel(ws, r, 4, 'Ανά κατάστημα', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Κατάστημα', key: 'store', width: 30 },
        { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Αξία', key: 'value', width: 14, align: 'right', fmt: EUR_FMT },
        { header: 'Μερίδιο', key: 'share', width: 12, align: 'right', fmt: PCT_FMT },
      ], wStore.map((s: any) => ({ store: s.store, qty: num(s.qty), value: num(s.value), share: wa.summary.value > 0 ? num(s.value) / wa.summary.value : 0 })), C.amber);
    }

    const wReason = (wa.byReason || []) as any[];
    if (wReason.length) {
      r = sectionLabel(ws, r, 3, 'Ανά λόγο', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Λόγος', key: 'reason', width: 24 },
        { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Αξία', key: 'value', width: 14, align: 'right', fmt: EUR_FMT },
      ], wReason.map((x: any) => ({ reason: W_REASONS[x.reason] || x.reason, qty: num(x.qty), value: num(x.value) })), C.amber);
    }

    const wDay = (wa.byDay || []) as any[];
    if (wDay.length) {
      r = sectionLabel(ws, r, 3, 'Ανά ημέρα', C.amber);
      r = writeTable(ws, r, 1, [
        { header: 'Ημερομηνία', key: 'day', width: 16 },
        { header: 'Τεμ.', key: 'qty', width: 10, align: 'right', fmt: INT_FMT },
        { header: 'Αξία', key: 'value', width: 14, align: 'right', fmt: EUR_FMT },
      ], wDay.map((d: any) => ({ day: d.day, qty: num(d.qty), value: num(d.value) })), C.amber);
    }
  }

  // ── Download ────────────────────────────────────────────────────────────────
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `analytics_${dateFrom}_${dateTo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
